// The Billing attribution card's model (SCR-585, REQ-231): API-580's answer as the card reads it —
// the headline share, the meter's three segments, each app-role reason with a count in words with
// its fix, the calls from proxies too old to report, and the installation rows by name. Pure and
// DOM-free (tests/unit/usage/attribution-model.test.ts).
//
// Nothing is computed that the server already decided: the share is its `shareBp`, the top reason
// its `topReason`. The meter's segments are the call counts as fractions of `requests`, which the
// three segments sum to (API-580's invariant). An installation is named by its `name`, never its id.
import type { components } from "../../api/schema";
import { formatPercentBp } from "../../primitives/money";
import { formatCount } from "./figures";

export type BillingAttribution = components["schemas"]["BillingAttributionResponse"];
type AttributionInstallation = components["schemas"]["BillingAttributionInstallation"];
export type AttributionReason = components["schemas"]["BillingAttributionReason"];
type AppRoleReasons = components["schemas"]["BillingAppRoleReasons"];

/** At or above this share (basis points) the card is a quiet success line with no reasons (SCR-585). */
export const HEALTHY_SHARE_BP = 9_900;

/** SCR-585's words for each reason, with what to do. Used verbatim; never reworded. */
export const REASON_TEXT: Readonly<Record<AttributionReason, string>> = {
  sts_denied: "AWS refused to start a person's session — the Bedrock role's trust policy must allow `sts:TagSession` for this proxy",
  sts_throttled: "AWS STS was busy — this clears on its own; if it persists, other workloads in the account are using the STS quota",
  sts_unavailable: "AWS STS did not answer",
  rate_limited: "The proxy limited how fast it asked AWS for sessions",
  no_person: "Calls by service credentials, which belong to no person",
  disabled: "Per-person attribution is turned off for this installation",
};

/** The order the reasons are listed in: the ones to act on first, the by-design ones last. */
const REASON_ORDER: readonly AttributionReason[] = ["sts_denied", "sts_throttled", "sts_unavailable", "rate_limited", "no_person", "disabled"];

/** The wire's camelCased key of each reason in `app.byReason`. */
const REASON_KEY: Readonly<Record<AttributionReason, keyof AppRoleReasons>> = {
  disabled: "disabled",
  no_person: "noPerson",
  sts_denied: "stsDenied",
  sts_throttled: "stsThrottled",
  sts_unavailable: "stsUnavailable",
  rate_limited: "rateLimited",
};

/** The short name of a reason in the installations table, where the full fix would not fit. */
const REASON_SHORT: Readonly<Record<AttributionReason, string>> = {
  sts_denied: "AWS refused the session",
  sts_throttled: "AWS STS was busy",
  sts_unavailable: "AWS STS did not answer",
  rate_limited: "Proxy rate limit",
  no_person: "Service credentials",
  disabled: "Attribution off",
};

export const ATTRIBUTION_TEXT = {
  title: "Billing attribution · last 24 hours",
  headline: "of Bedrock calls are billed to a person in AWS",
  noCalls: "No Bedrock calls in the last 24 hours",
  healthy: "Every call made for a person is billed to that person in AWS.",
  noShare: "None of these calls could be billed to a person: they came from service credentials, from an installation with attribution turned off, or from proxies that do not report attribution yet.",
  unknown: (calls: number) => `${callsWord(calls)} went out under the proxy's own role with no reason recorded`,
  setUp: "Set up AWS Billing",
} as const;

export interface ReasonLine {
  reason: AttributionReason;
  calls: number;
  text: string;
}

export type MeterSegmentKey = "person" | "app" | "notReported";

export interface MeterSegment {
  key: MeterSegmentKey;
  label: string;
  calls: number;
  /** The segment's part of all calls, 0–1; the three sum to 1. */
  fraction: number;
}

export interface InstallationRow {
  key: string;
  name: string;
  calls: number;
  share: string;
  topReason: string;
}

export type AttributionView =
  | { state: "no-calls" }
  | {
      state: "calls";
      /** `shareBp` as a percentage; "—" when no call counts towards it. */
      share: string;
      /** At or above HEALTHY_SHARE_BP: the quiet line instead of the reasons. */
      healthy: boolean;
      meter: MeterSegment[];
      /** The app-role reasons with a count, in REASON_ORDER; empty when healthy. */
      reasons: ReasonLine[];
      /** App-role calls with no reason (a proxy fault); null when none. */
      unknown: string | null;
      /** "N calls came from proxies that do not report attribution yet — upgrade them"; null when none. */
      notReported: string | null;
      installations: InstallationRow[];
    };

function callsWord(calls: number): string {
  return `${formatCount(calls)} ${calls === 1 ? "call" : "calls"}`;
}

/** SCR-585's older-proxies line. */
export function notReportedLine(calls: number): string {
  return `${callsWord(calls)} came from proxies that do not report attribution yet — upgrade them`;
}

function attributed(counts: { attributed: { person: number; awsPrincipal: number } }): number {
  return counts.attributed.person + counts.attributed.awsPrincipal;
}

/** The meter's three segments, as parts of every call; each segment's share of `requests`. */
export function meterSegments(status: Pick<BillingAttribution, "requests" | "attributed" | "app" | "notReported">): MeterSegment[] {
  const total = status.requests;
  const part = (calls: number) => (total > 0 ? calls / total : 0);
  return [
    { key: "person", label: "Billed to a person", calls: attributed(status), fraction: part(attributed(status)) },
    { key: "app", label: "The proxy's own role", calls: status.app.total, fraction: part(status.app.total) },
    { key: "notReported", label: "Not reported", calls: status.notReported, fraction: part(status.notReported) },
  ];
}

/** Each reason with a count, in words with its fix. */
export function reasonLines(byReason: AppRoleReasons): ReasonLine[] {
  return REASON_ORDER.filter((reason) => byReason[REASON_KEY[reason]] > 0).map((reason) => ({ reason, calls: byReason[REASON_KEY[reason]], text: REASON_TEXT[reason] }));
}

function installationRow(row: AttributionInstallation): InstallationRow {
  return {
    key: row.installationId,
    name: row.name,
    calls: row.requests,
    share: formatPercentBp(row.shareBp),
    topReason: row.topReason ? REASON_SHORT[row.topReason] : "—",
  };
}

/** The card's whole view of one API-580 answer. */
export function attributionView(status: BillingAttribution): AttributionView {
  if (status.requests === 0) return { state: "no-calls" };
  const healthy = status.shareBp !== null && status.shareBp >= HEALTHY_SHARE_BP;
  const unknown = status.app.byReason.unknown;
  return {
    state: "calls",
    share: formatPercentBp(status.shareBp),
    healthy,
    meter: meterSegments(status),
    reasons: healthy ? [] : reasonLines(status.app.byReason),
    unknown: unknown > 0 ? ATTRIBUTION_TEXT.unknown(unknown) : null,
    notReported: status.notReported > 0 ? notReportedLine(status.notReported) : null,
    installations: status.installations.map(installationRow),
  };
}
